// ===========================================
// SETTINGS → INTEGRATIONS
// ===========================================
// Every connection Mise offers, in one place: what it does, whether it's
// connected, and the connect/disconnect actions — instead of hunting for the
// connect card inside whichever feature the integration feeds. The in-context
// cards (Production → Orders, Wholesale → Orders) still exist; this is the
// directory.

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShoppingBag, Store, CalendarClock, CalendarDays, CreditCard, Check, Loader2 , BookOpenCheck, Mail, Instagram as InstagramIcon, Bike, Truck, Zap, Landmark, Receipt, Boxes, Warehouse } from 'lucide-react';
import api, { productionApi, wholesaleApi, restaurantApi } from '../services/api';
import { readIntegrationOAuthResult } from '../utils/integrationOAuth';
import { channelMoney } from '../utils/channelMoney';
import { Link } from 'react-router-dom';

export default function IntegrationsSettings({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();

  // Show only the returning provider's result, then clear its callback fields.
  useEffect(() => {
    const result = readIntegrationOAuthResult(window.location.search);
    if (!result.notifications.length) return;
    for (const notification of result.notifications) {
      say(notification.tone, notification.message);
      qc.invalidateQueries({ queryKey: [notification.queryKey] });
    }
    window.history.replaceState({}, '', `${window.location.pathname}${result.search ? `?${result.search}` : ''}${window.location.hash}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Shopify
  const { data: shopify } = useQuery({
    queryKey: ['production-shopify'],
    queryFn: async () => (await productionApi.getShopifyStatus()).data?.data || { connected: false },
  });
  const [shopDomain, setShopDomain] = useState('');
  const [shopToken, setShopToken] = useState('');
  const [shopClientId, setShopClientId] = useState('');
  const [shopClientSecret, setShopClientSecret] = useState('');
  const startShopifyOauth = useMutation({
    mutationFn: () => productionApi.startShopifyOauth(shopDomain.trim()),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.url;
      if (url) window.location.href = url;
      else say('error', "Couldn't start the Shopify connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Shopify connection."),
  });
  const connectShopifyToken = useMutation({
    mutationFn: () => productionApi.connectShopify({ shopDomain: shopDomain.trim(), accessToken: shopToken.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-shopify'] }); setShopToken(''); say('success', 'Shopify connected.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect to Shopify."),
  });
  const connectShopifyApp = useMutation({
    mutationFn: () => productionApi.connectShopifyApp({ shopDomain: shopDomain.trim(), clientId: shopClientId.trim(), clientSecret: shopClientSecret.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-shopify'] }); setShopClientSecret(''); setShopClientId(''); say('success', 'Shopify connected. Access renews automatically.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect this Shopify app."),
  });
  const disconnectShopify = useMutation({
    mutationFn: () => productionApi.disconnectShopify(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-shopify'] }); setShopSummary(null); say('success', 'Shopify disconnected. Synced orders stay; nothing new comes in.'); },
    onError: () => say('error', "Couldn't disconnect Shopify."),
  });
  const [shopSummary, setShopSummary] = useState<any | null>(null);
  const shopifySummary = useMutation({
    mutationFn: () => productionApi.getShopifySalesSummary(),
    onSuccess: (res: any) => {
      const d = res.data?.data;
      setShopSummary(d || null);
      if (d && d.orderCount === 0) say('success', `Connected and reading fine — no orders since ${d.sinceDate} yet.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't fetch the sales summary."),
  });

  // ---- Faire
  const { data: faire } = useQuery({
    queryKey: ['wholesale-faire-status'],
    queryFn: async () => (await wholesaleApi.getFaireStatus()).data?.data || null,
  });
  const [faireToken, setFaireToken] = useState('');
  const connectFaireToken = useMutation({
    mutationFn: () => wholesaleApi.connectFaire('', '', faireToken.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] }); setFaireToken(''); say('success', 'Faire connected — the token works.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect Faire."),
  });
  // Platform-app OAuth: save the platform credentials on this account, then
  // straight to Faire's approve screen — the same one-click as Shopify's.
  const startFaireOauth = useMutation({
    mutationFn: () => wholesaleApi.connectFaire('', ''),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.authorizeUrl;
      if (url) window.location.href = url;
      else { qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] }); say('error', "Couldn't start the Faire connection."); }
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Faire connection."),
  });
  const disconnectFaire = useMutation({
    mutationFn: () => wholesaleApi.disconnectFaire(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['wholesale-faire-status'] }); say('success', 'Faire disconnected. Imported orders stay; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect Faire."),
  });
  const syncFaire = useMutation({
    mutationFn: () => wholesaleApi.syncFaire(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['wholesale-orders'] });
      say('success', `Faire sync: ${d.created} new order${d.created === 1 ? '' : 's'} (${d.skippedExisting} already in).`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Faire sync failed.'),
  });

  // ---- 7shifts
  const { data: seven } = useQuery({
    queryKey: ['sevenshifts-status'],
    queryFn: async () => (await wholesaleApi.getSevenShiftsStatus()).data?.data || { connected: false },
  });
  const [sevenToken, setSevenToken] = useState('');
  const startSevenOauth = useMutation({
    mutationFn: () => wholesaleApi.startSevenShiftsOauth(),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.url;
      if (url) window.location.href = url;
      else say('error', "Couldn't start the 7shifts connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the 7shifts connection."),
  });
  const connectSeven = useMutation({
    mutationFn: () => wholesaleApi.connectSevenShifts(sevenToken.trim()),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sevenshifts-status'] }); setSevenToken(''); say('success', '7shifts connected.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect 7shifts."),
  });
  const disconnectSeven = useMutation({
    mutationFn: () => wholesaleApi.disconnectSevenShifts(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sevenshifts-status'] }); say('success', '7shifts disconnected. Imported shifts stay; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect 7shifts."),
  });
  const syncSeven = useMutation({
    mutationFn: () => wholesaleApi.syncSevenShifts(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['sevenshifts-status'] });
      const um = (d.unmatchedPeople || []).length;
      say(um ? 'error' : 'success', `7shifts sync: ${d.instances} shifts, ${d.assignments} assignments over the next two weeks.${um ? ` ${um} people aren't Mise employees yet (${d.unmatchedPeople.slice(0, 4).join(', ')}${um > 4 ? '…' : ''}) — add them, then sync again.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || '7shifts sync failed.'),
  });

  // ---- Square
  const { data: square } = useQuery({
    queryKey: ['square-status'],
    queryFn: async () => (await wholesaleApi.getSquareStatus()).data?.data || { connected: false },
  });
  const startSquareOauth = useMutation({
    mutationFn: () => wholesaleApi.startSquareOauth(),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.url;
      if (url) window.location.href = url;
      else say('error', "Couldn't start the Square connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Square connection."),
  });
  const disconnectSquare = useMutation({
    mutationFn: () => wholesaleApi.disconnectSquare(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['square-status'] }); say('success', 'Square disconnected.'); },
    onError: () => say('error', "Couldn't disconnect Square."),
  });
  const importSquareMenu = useMutation({
    mutationFn: () => wholesaleApi.importSquareMenu(),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['menu-categories'] });
      say('success', `Menu imported from Square — ${d.items || 0} items in ${d.categories || 0} new categories${d.skippedExisting ? ` (${d.skippedExisting} already existed)` : ''}. Review it on the Menu page.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't read the Square catalog."),
  });
  const syncSquare = useMutation({
    mutationFn: () => wholesaleApi.syncSquare(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['square-status'] });
      const tot = (d.days || []).reduce((s: number, x: any) => s + x.grossCents, 0);
      const n = (d.days || []).reduce((s: number, x: any) => s + x.payments, 0);
      say('success', `Square: ${n} payments, $${(tot / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} gross over the last 7 days (${(d.locationNames || []).join(', ')}). Read-only for now — say where these sales should live in Mise.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Square sync failed.'),
  });

  // ---- Delivery platforms (Uber Eats + DoorDash)
  const { data: ubereats } = useQuery({
    queryKey: ['delivery-ubereats'],
    queryFn: async () => (await wholesaleApi.getDeliveryStatus('ubereats')).data?.data || { connected: false },
  });
  const { data: doordash } = useQuery({
    queryKey: ['delivery-doordash'],
    queryFn: async () => (await wholesaleApi.getDeliveryStatus('doordash')).data?.data || { connected: false },
  });
  const startUbereats = useMutation({
    mutationFn: () => wholesaleApi.startUbereatsOauth(),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.url;
      if (url) window.location.href = url;
      else say('error', "Couldn't start the Uber Eats connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Uber Eats connection."),
  });
  const connectDoordash = useMutation({
    mutationFn: () => wholesaleApi.connectDoordash(),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['delivery-doordash'] });
      const names = (res?.data?.data?.businesses || []).map((b: any) => b.name).join(', ');
      say('success', `DoorDash connected${names ? ` (${names})` : ''}. Sales pull in as DoorDash grants marketplace data access.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect DoorDash."),
  });
  const syncDelivery = (platform: string, label: string) => ({
    mutationFn: () => wholesaleApi.syncDelivery(platform),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: [`delivery-${platform}`] });
      if (d.note) { say('success', d.note); return; }
      const tot = (d.days || []).reduce((s2: number, x: any) => s2 + x.grossCents, 0);
      const n = (d.days || []).reduce((s2: number, x: any) => s2 + x.orders, 0);
      say('success', `${label}: ${n} orders, $${(tot / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} gross over the last 7 days (${(d.storeNames || []).join(', ')}).`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || `${label} sync failed.`),
  });
  const syncUbereats = useMutation(syncDelivery('ubereats', 'Uber Eats'));
  const syncDoordash = useMutation(syncDelivery('doordash', 'DoorDash'));
  const disconnectDelivery = useMutation({
    mutationFn: (platform: string) => wholesaleApi.disconnectDelivery(platform),
    onSuccess: (_r, platform) => { qc.invalidateQueries({ queryKey: [`delivery-${platform}`] }); say('success', 'Disconnected.'); },
    onError: () => say('error', "Couldn't disconnect."),
  });

  // ---- QuickBooks
  const { data: qbo } = useQuery({
    queryKey: ['quickbooks-status'],
    queryFn: async () => (await wholesaleApi.getQuickbooksStatus()).data?.data || { connected: false },
  });
  const startQboOauth = useMutation({
    mutationFn: () => wholesaleApi.startQuickbooksOauth(),
    onSuccess: (res) => {
      const url = res.data?.data?.url;
      if (url) window.location.href = url;
      else say('error', "Couldn't start the QuickBooks connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the QuickBooks connection."),
  });
  const disconnectQbo = useMutation({
    mutationFn: () => wholesaleApi.disconnectQuickbooks(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['quickbooks-status'] }); say('success', 'QuickBooks disconnected.'); },
    onError: () => say('error', "Couldn't disconnect QuickBooks."),
  });
  const syncQbo = useMutation({
    mutationFn: () => wholesaleApi.syncQuickbooks(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['quickbooks-status'] });
      const bits = [`${d.invoicesCreated || 0} invoice${d.invoicesCreated === 1 ? '' : 's'} created`,
        d.invoicesAlreadyThere ? `${d.invoicesAlreadyThere} already there` : '',
        `${d.paymentsRecorded || 0} payment${d.paymentsRecorded === 1 ? '' : 's'} recorded`].filter(Boolean).join(', ');
      const skipped = (d.skipped || []).length;
      say(skipped ? 'error' : 'success', `QuickBooks: ${bits}${skipped ? `; ${skipped} skipped (${d.skipped[0].invoiceNumber}: ${d.skipped[0].reason})` : '.'}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'QuickBooks sync failed.'),
  });

  // ---- Gmail
  const { data: gmail } = useQuery({
    queryKey: ['gmail-status'],
    queryFn: async () => (await wholesaleApi.getGmailStatus()).data?.data || { connected: false },
  });
  const startGmailOauth = useMutation({
    mutationFn: () => wholesaleApi.startGmailOauth(),
    onSuccess: (res) => { const url = res.data?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Gmail connection."); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Gmail connection."),
  });
  const disconnectGmail = useMutation({
    mutationFn: () => wholesaleApi.disconnectGmail(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['gmail-status'] }); say('success', 'Gmail disconnected.'); },
  });
  const syncGmail = useMutation({
    mutationFn: () => wholesaleApi.syncGmail(),
    onSuccess: (res) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['gmail-status'] });
      qc.invalidateQueries({ queryKey: ['action-items'] });
      say('success', `Gmail: scanned ${d.scanned || 0} recent emails, ${d.created || 0} new to-do${d.created === 1 ? '' : 's'} on your dashboard.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Gmail sync failed.'),
  });
  const { data: instagram } = useQuery({
    queryKey: ['instagram-status'],
    queryFn: async () => (await wholesaleApi.getInstagramStatus()).data?.data || { connected: false },
  });

  // ---- Tripleseat
  const { data: tripleseat } = useQuery({
    queryKey: ['tripleseat-status'],
    queryFn: async () => (await api.get('/v1/integrations/tripleseat/status')).data?.data || { connected: false },
  });
  const [tsClientId, setTsClientId] = useState('');
  const [tsClientSecret, setTsClientSecret] = useState('');
  const [tsToken, setTsToken] = useState('');
  const startTripleseatOauth = useMutation({
    mutationFn: async () => (await api.get('/v1/integrations/tripleseat/oauth/start')).data,
    onSuccess: (res: any) => { const url = res?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Tripleseat connection."); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Tripleseat connection."),
  });
  // The venue's own OAuth app (made in THEIR Tripleseat Settings > API) —
  // credentials save first, then straight to Tripleseat's approve screen.
  const startTripleseatWithCreds = useMutation({
    mutationFn: async () => (await api.post('/v1/integrations/tripleseat/oauth/start-with-credentials', { clientId: tsClientId.trim(), clientSecret: tsClientSecret.trim() })).data,
    onSuccess: (res: any) => { const url = res?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Tripleseat connection."); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Tripleseat connection."),
  });
  const connectTripleseatToken = useMutation({
    mutationFn: () => api.post('/v1/integrations/tripleseat/connect', {
      accessToken: tsToken.trim(),
      ...(tsClientId.trim() ? { clientId: tsClientId.trim() } : {}),
      ...(tsClientSecret.trim() ? { clientSecret: tsClientSecret.trim() } : {}),
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['tripleseat-status'] });
      setTsToken('');
      say('success', `Tripleseat connected${res.data?.data?.siteName ? ` (${res.data.data.siteName})` : ''} — the token works. Hit Sync now and your events appear in Events.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Tripleseat didn't accept that token."),
  });
  const syncTripleseat = useMutation({
    mutationFn: () => api.post('/v1/integrations/tripleseat/sync', {}),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['tripleseat-status'] });
      qc.invalidateQueries({ queryKey: ['events'] });
      say('success', `Tripleseat: ${d.events || 0} events and ${d.leads || 0} leads read — ${d.created || 0} new in Events, ${d.updated || 0} refreshed${d.cancelled ? `, ${d.cancelled} cancelled` : ''}.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Tripleseat sync failed.'),
  });
  const disconnectTripleseat = useMutation({
    mutationFn: () => api.delete('/v1/integrations/tripleseat/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tripleseat-status'] }); say('success', 'Tripleseat disconnected. Synced events stay in Events; nothing new comes in.'); },
    onError: () => say('error', "Couldn't disconnect Tripleseat."),
  });

  // CPG-only connectors show for accounts that read as CPG — the same signals
  // Layout keys the Hub pages off.
  const { data: fullRestaurant } = useQuery({
    queryKey: ['restaurant-current-settings'],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30 * 1000,
  });
  const rs = (fullRestaurant?.settings as any) || {};
  const isCpgAccount = rs.businessType === 'cpg' || rs.businessKind === 'cpg' || !!rs.supplyRatios || !!rs.channelForecast;

  const input = 'input w-full text-sm';

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-slate-500">Connect the tools you already sell through — each one says what it feeds inside Mise. <span className="text-slate-400">Connected tools refresh themselves every 30 minutes; the buttons just force it sooner.</span></p>
      <p className="text-xs text-slate-400">
        Two ways in: <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium whitespace-nowrap">Sign-in (OAuth)</span> opens the provider's own approval screen, and <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium whitespace-nowrap">Paste a key</span> takes credentials you create in your own account — checked against the provider before anything saves. Most cards offer both; a card says so when the provider only allows one.
      </p>

      {/* SHOPIFY */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center shrink-0"><ShoppingBag className="w-5 h-5 text-green-600" /></div>
            <div>
              <p className="font-semibold text-slate-900">Shopify</p>
              <p className="text-xs text-slate-500">View Shopify listings in <b>Products</b>, open unfulfilled orders in <b>Production → All orders</b>, and recent sales below. Warehouse stock and accounting records use their own connections.</p>
            </div>
          </div>
          {shopify?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {shopify.shopDomain}</span>}
        </div>
        {shopify?.connected ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
              <span>Last synced {shopify.lastSyncAt ? new Date(shopify.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
              <button onClick={() => shopifySummary.mutate()} disabled={shopifySummary.isPending} className="btn btn-sm btn-secondary">
                {shopifySummary.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Last 7 days'}
              </button>
              <Link to="/products" className="btn btn-sm btn-secondary">View products</Link>
              <Link to="/production?tab=orders" className="btn btn-sm btn-secondary">View open orders</Link>
              <button onClick={() => disconnectShopify.mutate()} disabled={disconnectShopify.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
            </div>
            {shopSummary?.currencyBasis === 'customer' && (
              <div className="rounded-xl bg-slate-50 p-3 space-y-3 text-xs">
                <p className="font-semibold text-slate-700">{shopSummary.orderCount.toLocaleString('en-US')} orders since {shopSummary.sinceDate}
                  {shopSummary.truncated && <span className="text-amber-600 font-normal"> (first 1,000 orders — totals are partial)</span>}
                </p>
                {(shopSummary.currencies || []).map((group: any) => <details key={group.currency}>
                  <summary className="cursor-pointer font-medium text-slate-700">{channelMoney(group.grossCents / 100, group.currency)} · {group.orderCount} {group.currency} orders</summary>
                  <div className="mt-2 space-y-1">{group.days.map((d: any) => <div key={d.date} className="flex flex-wrap items-baseline gap-3 text-slate-600">
                    <span className="w-20 text-slate-400">{new Date(`${d.date}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
                    <span>{d.orders} orders</span><span className="font-medium">{channelMoney(d.grossCents / 100, group.currency)}</span>
                  </div>)}</div>
                </details>)}
                <p className="text-slate-500">Original order currencies, shown separately. Store base: {shopSummary.storeCurrency || 'unavailable'}. No currency conversion; these sales aren't moved into Mise's books yet.</p>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <input value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} placeholder="Store domain, e.g. your-store.myshopify.com" className={input} />
            {shopify?.oauthAvailable && (
              <div className="flex items-center gap-2">
                <button onClick={() => startShopifyOauth.mutate()} disabled={!shopDomain.trim() || startShopifyOauth.isPending} className="btn btn-sm btn-primary">
                  {startShopifyOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Shopify'}
                </button>
                <span className="text-xs text-slate-400">You'll approve access in your Shopify admin.</span>
              </div>
            )}
            <details className="text-sm">
              <summary className="cursor-pointer font-medium text-slate-700">Connect an app created for your store</summary>
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500">In Shopify’s <a href="https://dev.shopify.com/dashboard" target="_blank" rel="noreferrer" className="underline">Dev Dashboard</a>, open the app created in your store’s organization. It must be installed on this store with <b>read_orders</b> and <b>read_products</b> access. Copy its client ID and secret from Settings. Mise checks order access before saving and renews access automatically.</p>
                <input value={shopClientId} onChange={(e) => setShopClientId(e.target.value)} aria-label="Shopify app client ID" placeholder="Client ID" autoComplete="off" className={input} />
                <input value={shopClientSecret} onChange={(e) => setShopClientSecret(e.target.value)} aria-label="Shopify app client secret" placeholder="Client secret" type="password" autoComplete="off" className={input} />
                <button onClick={() => connectShopifyApp.mutate()} disabled={!shopDomain.trim() || !shopClientId.trim() || !shopClientSecret.trim() || connectShopifyApp.isPending} className="btn btn-sm btn-primary">{connectShopifyApp.isPending ? 'Checking…' : 'Connect store app'}</button>
              </div>
            </details>
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500">Use an existing legacy access token</summary>
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500">For an older custom app with a permanent Admin API token. If your app gives you a client ID and secret, use the option above.</p>
                <div className="flex items-center gap-2">
                  <input value={shopToken} onChange={(e) => setShopToken(e.target.value)} placeholder="Admin API access token (shpat_…)" type="password" className={input} />
                  <button onClick={() => connectShopifyToken.mutate()} disabled={!shopDomain.trim() || !shopToken.trim() || connectShopifyToken.isPending} className="btn btn-sm btn-primary shrink-0">{connectShopifyToken.isPending ? 'Checking…' : 'Connect'}</button>
                </div>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* FAIRE */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><Store className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="font-semibold text-slate-900">Faire</p>
              <p className="text-xs text-slate-500">Brings your Faire wholesale orders into <b>Wholesale</b> under a "Faire" customer — Command Center cards, Channels revenue, the lot. Values are gross wholesale; Faire's commission comes out at payout.</p>
            </div>
          </div>
          {faire?.authorized && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> Connected</span>}
        </div>
        {faire?.authorized ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {faire.lastSyncAt ? new Date(faire.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncFaire.mutate()} disabled={syncFaire.isPending} className="btn btn-sm btn-secondary">{syncFaire.isPending ? 'Syncing…' : 'Refresh'}</button>
            <button onClick={() => disconnectFaire.mutate()} disabled={disconnectFaire.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : (
          <div className="space-y-2">
            {faire?.connected && faire?.authorizeUrl ? (
              <div className="flex items-center gap-2">
                <a href={faire.authorizeUrl} className="btn btn-sm btn-primary">Log in to Faire &amp; approve</a>
                <span className="text-xs text-slate-400">Approve while logged into the brand's Faire account.</span>
              </div>
            ) : !faire?.connected && faire?.platformApp ? (
              <div className="flex items-center gap-2">
                <button onClick={() => startFaireOauth.mutate()} disabled={startFaireOauth.isPending} className="btn btn-sm btn-primary">
                  {startFaireOauth.isPending ? 'Redirecting…' : 'Connect with Faire'}
                </button>
                <span className="text-xs text-slate-400">You'll approve access in the Faire brand portal.</span>
              </div>
            ) : null}
            <p className="text-xs text-slate-500">A <b>Faire API access token</b> issued for your brand (in the Faire brand portal, or from Faire support) connects directly — Mise reads one page of your orders to verify before saving.</p>
            <div className="flex items-center gap-2">
              <input value={faireToken} onChange={(e) => setFaireToken(e.target.value)} placeholder="Faire API access token" type="password" className={input} />
              <button onClick={() => connectFaireToken.mutate()} disabled={!faireToken.trim() || connectFaireToken.isPending} className="btn btn-sm btn-primary shrink-0">{connectFaireToken.isPending ? 'Checking…' : 'Connect'}</button>
            </div>
            {(faire?.connected || faire?.authorized) && (
              <button onClick={() => disconnectFaire.mutate()} disabled={disconnectFaire.isPending} className="text-xs text-slate-400 hover:text-red-500">Disconnect / start over</button>
            )}
          </div>
        )}
      </div>

      {/* 7SHIFTS */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><CalendarDays className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="font-semibold text-slate-900">7shifts</p>
              <p className="text-xs text-slate-500">Keep scheduling in 7shifts — Mise pulls the next two weeks of published shifts into <b>Scheduling</b>, so labor-vs-sales, pre-shift, and who's-working stay accurate. People are matched to Mise employees by email or name (never invented).</p>
            </div>
          </div>
          {seven?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> Connected</span>}
        </div>
        {seven?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {seven.lastSyncAt ? new Date(seven.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncSeven.mutate()} disabled={syncSeven.isPending} className="btn btn-sm btn-secondary">{syncSeven.isPending ? 'Syncing…' : 'Refresh'}</button>
            <button onClick={() => disconnectSeven.mutate()} disabled={disconnectSeven.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : (
          <div className="space-y-2">
            {seven?.oauthAvailable && (
              <div className="flex items-center gap-2">
                <button onClick={() => startSevenOauth.mutate()} disabled={startSevenOauth.isPending} className="btn btn-sm btn-primary">
                  {startSevenOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with 7shifts'}
                </button>
                <span className="text-xs text-slate-400">You'll approve access on 7shifts' consent screen.</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input value={sevenToken} onChange={(e) => setSevenToken(e.target.value)} placeholder={seven?.oauthAvailable ? 'Or paste an access token (Company Settings → Developer tools)' : '7shifts access token (Company Settings → Developer tools / API)'} type="password" className={input} />
              <button onClick={() => connectSeven.mutate()} disabled={!sevenToken.trim() || connectSeven.isPending} className="btn btn-sm btn-secondary shrink-0">{connectSeven.isPending ? 'Checking…' : 'Connect'}</button>
            </div>
          </div>
        )}
      </div>

      {/* SQUARE */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-white" /></div>
            <div>
              <p className="font-semibold text-slate-900">Square</p>
              <p className="text-xs text-slate-500">For businesses selling on Square POS — connect and Mise reads your sales (last-7-days summary for now; deeper ingestion lands where you want it). Tokens auto-refresh.</p>
            </div>
          </div>
          {square?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> Connected</span>}
        </div>
        {square?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {square.lastSyncAt ? new Date(square.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncSquare.mutate()} disabled={syncSquare.isPending} className="btn btn-sm btn-secondary">{syncSquare.isPending ? 'Syncing…' : 'Refresh'}</button>
            <button onClick={() => importSquareMenu.mutate()} disabled={importSquareMenu.isPending} className="btn btn-sm btn-secondary" title="Your Square catalog becomes the Mise menu — one tap, nothing overwritten">{importSquareMenu.isPending ? 'Importing…' : 'Import menu from Square'}</button>
            <button onClick={() => disconnectSquare.mutate()} disabled={disconnectSquare.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : square?.oauthAvailable ? (
          <div className="space-y-2">
            <ol className="text-xs text-slate-500 list-decimal ml-4 space-y-0.5">
              <li>Have the <b>Square account owner's login</b> ready (whoever owns the Square dashboard).</li>
              <li>Click <b>Connect with Square</b> — Square's own sign-in opens.</li>
              <li>Press <b>Allow</b> on their consent screen. Done — tokens refresh themselves, nothing to paste, ever.</li>
              <li>Repeat per store if each location is its own Square account; one account with multiple locations connects once.</li>
            </ol>
            <button onClick={() => startSquareOauth.mutate()} disabled={startSquareOauth.isPending} className="btn btn-sm btn-primary">
              {startSquareOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Square'}
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-400">Square OAuth isn't configured on the platform yet.</p>
        )}
      </div>

      {/* UBER EATS */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-900 flex items-center justify-center shrink-0"><Bike className="w-5 h-5 text-white" /></div>
            <div>
              <p className="font-semibold text-slate-900">Uber Eats</p>
              <p className="text-xs text-slate-500">For shops selling on Uber Eats — connect and Mise reads your delivery sales next to everything else (last-7-days summary first; deeper ingestion lands where you want it). Tokens auto-refresh.</p>
            </div>
          </div>
          {ubereats?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> Connected</span>}
        </div>
        {ubereats?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {ubereats.lastSyncAt ? new Date(ubereats.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncUbereats.mutate()} disabled={syncUbereats.isPending} className="btn btn-sm btn-secondary">{syncUbereats.isPending ? 'Syncing…' : 'Refresh'}</button>
            <button onClick={() => disconnectDelivery.mutate('ubereats')} disabled={disconnectDelivery.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : ubereats?.oauthAvailable ? (
          <div className="flex items-center gap-2">
            <button onClick={() => startUbereats.mutate()} disabled={startUbereats.isPending} className="btn btn-sm btn-primary">
              {startUbereats.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Uber Eats'}
            </button>
            <span className="text-xs text-slate-400">You'll approve access on Uber's consent screen.</span>
          </div>
        ) : (
          <p className="text-xs text-slate-400">Uber Eats access isn't configured on the platform yet — it arrives with Mise's Uber Eats API approval.</p>
        )}
      </div>

      {/* DOORDASH */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-600 flex items-center justify-center shrink-0"><ShoppingBag className="w-5 h-5 text-white" /></div>
            <div>
              <p className="font-semibold text-slate-900">DoorDash</p>
              <p className="text-xs text-slate-500">For shops selling on DoorDash — connects through Mise's DoorDash developer account and pulls what DoorDash makes available (order-level sales unlock with their Marketplace partner access; Mise is ready the day they grant it).</p>
            </div>
          </div>
          {doordash?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {doordash.displayName || 'Connected'}</span>}
        </div>
        {doordash?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {doordash.lastSyncAt ? new Date(doordash.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncDoordash.mutate()} disabled={syncDoordash.isPending} className="btn btn-sm btn-secondary">{syncDoordash.isPending ? 'Syncing…' : 'Refresh'}</button>
            <button onClick={() => disconnectDelivery.mutate('doordash')} disabled={disconnectDelivery.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : doordash?.oauthAvailable ? (
          <div className="flex items-center gap-2">
            <button onClick={() => connectDoordash.mutate()} disabled={connectDoordash.isPending} className="btn btn-sm btn-primary">
              {connectDoordash.isPending ? 'Checking…' : 'Connect DoorDash'}
            </button>
            <span className="text-xs text-slate-400">Validates against DoorDash and links your business.</span>
          </div>
        ) : (
          <p className="text-xs text-slate-400">DoorDash access isn't configured on the platform yet — it arrives with Mise's DoorDash developer approval.</p>
        )}
      </div>

      {/* QUICKBOOKS */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><BookOpenCheck className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="font-semibold text-slate-900">QuickBooks Online</p>
              <p className="text-xs text-slate-500">Pushes your <b>Wholesale</b> invoices and recorded payments into the books — invoices carry their Mise numbers, so re-syncing never duplicates. Short-paid remainders stay open in QuickBooks for your accountant's credit memo, matching the deduction here.</p>
            </div>
          </div>
          {qbo?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {qbo.companyName || 'Connected'}</span>}
        </div>
        {qbo?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {qbo.lastSyncAt ? new Date(qbo.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncQbo.mutate()} disabled={syncQbo.isPending} className="btn btn-sm btn-secondary">{syncQbo.isPending ? 'Pushing…' : 'Refresh'}</button>
            <button onClick={() => disconnectQbo.mutate()} disabled={disconnectQbo.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : qbo?.oauthAvailable ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <button onClick={() => startQboOauth.mutate()} disabled={startQboOauth.isPending} className="btn btn-sm btn-primary">
                {startQboOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with QuickBooks'}
              </button>
              <span className="text-xs text-slate-400">You'll approve access on Intuit's consent screen.</span>
            </div>
            <p className="text-xs text-slate-400">QuickBooks connects by signing in — Intuit doesn't issue API keys to paste.</p>
          </div>
        ) : (
          <p className="text-xs text-slate-400">QuickBooks OAuth isn't configured on the platform yet. Signing in is the only path Intuit offers — they don't issue API keys to paste.</p>
        )}
      </div>

      {/* GMAIL */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-red-500" /></div>
            <div>
              <p className="font-semibold text-slate-900">Gmail</p>
              <p className="text-xs text-slate-500">Reads recent operational email — POs, remittances, deduction notices, buyer inquiries — and turns each into a to-do on your dashboard, linking back to the message. Metadata only; bodies and attachments stay in Gmail.</p>
            </div>
          </div>
          {gmail?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {gmail.email || 'Connected'}</span>}
        </div>
        {gmail?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {gmail.lastSyncAt ? new Date(gmail.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncGmail.mutate()} disabled={syncGmail.isPending} className="btn btn-sm btn-secondary">{syncGmail.isPending ? 'Reading…' : 'Refresh'}</button>
            <button onClick={() => disconnectGmail.mutate()} disabled={disconnectGmail.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : gmail?.oauthAvailable ? (
          <div className="flex items-center gap-2">
            <button onClick={() => startGmailOauth.mutate()} disabled={startGmailOauth.isPending} className="btn btn-sm btn-primary">
              {startGmailOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Google'}
            </button>
            <span className="text-xs text-slate-400">You'll approve read-only access on Google's consent screen.</span>
          </div>
        ) : (
          <p className="text-xs text-slate-400">Google OAuth isn't configured on the platform yet.</p>
        )}
      </div>

      {/* TRIPLESEAT — events & leads flow into Mise Events */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5 text-violet-600" /></div>
            <div>
              <p className="font-semibold text-slate-900">Tripleseat</p>
              <p className="text-xs text-slate-500">Keep selling in Tripleseat — your events and leads flow into <b>Events</b> with pipeline status, client details, room, guest counts and money, so staffing, prep and the day-of run happen here. Changes in Tripleseat land in real time.</p>
            </div>
          </div>
          {tripleseat?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1 shrink-0"><Check className="w-3 h-3" /> {tripleseat.siteName || 'Connected'}</span>}
        </div>
        {tripleseat?.connected ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <span>Last synced {tripleseat.lastSyncAt ? new Date(tripleseat.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => syncTripleseat.mutate()} disabled={syncTripleseat.isPending} className="btn btn-sm btn-secondary">{syncTripleseat.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnectTripleseat.mutate()} disabled={disconnectTripleseat.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        ) : (
          <div className="space-y-2">
            {tripleseat?.oauthAvailable && (
              <div className="flex items-center gap-2">
                <button onClick={() => startTripleseatOauth.mutate()} disabled={startTripleseatOauth.isPending} className="btn btn-sm btn-primary">
                  {startTripleseatOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect to Tripleseat'}
                </button>
                <span className="text-xs text-slate-400">You'll approve access as a Tripleseat customer admin.</span>
              </div>
            )}
            {tripleseat?.credentialsSaved ? (
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => startTripleseatOauth.mutate()} disabled={startTripleseatOauth.isPending} className="btn btn-sm btn-primary">
                  {startTripleseatOauth.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Approve in Tripleseat'}
                </button>
                <span className="text-xs text-slate-400">Your credentials are saved — one approval in Tripleseat finishes the connection.</span>
                <button onClick={() => disconnectTripleseat.mutate()} className="text-xs text-slate-400 hover:text-red-500">Start over</button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500">{tripleseat?.oauthAvailable ? 'Or use' : 'Use'} <b>your own Tripleseat API credentials</b> — in Tripleseat go to <b>Settings &gt; API</b>, create credentials, and set the redirect URI to:</p>
                {tripleseat?.redirectUri && <code className="block text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 break-all select-all">{tripleseat.redirectUri}</code>}
                <div className="flex gap-2 flex-wrap">
                  <input value={tsClientId} onChange={(e) => setTsClientId(e.target.value)} placeholder="Client ID" className="input input-sm w-44" />
                  <input value={tsClientSecret} onChange={(e) => setTsClientSecret(e.target.value)} placeholder="Client secret" type="password" className="input input-sm w-52" />
                  <button onClick={() => startTripleseatWithCreds.mutate()} disabled={!tsClientId.trim() || !tsClientSecret.trim() || startTripleseatWithCreds.isPending} className="btn btn-sm btn-primary">
                    {startTripleseatWithCreds.isPending ? 'Saving…' : 'Connect with your credentials'}
                  </button>
                </div>
              </>
            )}
            <div className="flex items-center gap-2">
              <input value={tsToken} onChange={(e) => setTsToken(e.target.value)} placeholder="Or paste an access token from Settings > API" type="password" className={input} />
              <button onClick={() => connectTripleseatToken.mutate()} disabled={!tsToken.trim() || connectTripleseatToken.isPending} className="btn btn-sm btn-secondary shrink-0">{connectTripleseatToken.isPending ? 'Checking…' : 'Use token'}</button>
            </div>
            {!tripleseat?.oauthAvailable && (
              <p className="text-xs text-slate-400">Prefer one click? The partner connect button appears here the moment Tripleseat approves our app — your own credentials work today.</p>
            )}
          </div>
        )}
      </div>

      {/* CPG CONNECTORS — ledgers + 3PL warehouses, for brand accounts only */}
      {isCpgAccount && (
        <>
          {/* E-CONOMIC — Danish ledger, agreement-token connect */}
          <div className="card p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Landmark className="w-5 h-5 text-indigo-600" /></div>
              <div className="flex-1 min-w-0"><EconomicCardBody say={say} /></div>
            </div>
          </div>

          {/* BILL.COM — US AP/AR, session-login connect */}
          <div className="card p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0"><Receipt className="w-5 h-5 text-orange-600" /></div>
              <div className="flex-1 min-w-0"><BillCardBody say={say} /></div>
            </div>
          </div>

          {/* LOGIWA WMS — US 3PL stock feed */}
          <div className="card p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center shrink-0"><Boxes className="w-5 h-5 text-cyan-600" /></div>
              <div className="flex-1 min-w-0"><LogiwaCardBody say={say} /></div>
            </div>
          </div>

          {/* ONGOING WMS — EU 3PL stock feed */}
          <div className="card p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-teal-50 flex items-center justify-center shrink-0"><Warehouse className="w-5 h-5 text-teal-600" /></div>
              <div className="flex-1 min-w-0"><OngoingCardBody say={say} /></div>
            </div>
          </div>
        </>
      )}

      {/* SUPPLIER PORTALS — invoices flow straight from distributor accounts */}
      <SupplierPortalCard slug="pfs" name="Performance Foodservice" say={say} />
      <SupplierPortalCard slug="baldor" name="Baldor Specialty Foods" say={say} />
      <SupplierPortalCard slug="driscoll" name="Driscoll Foods" say={say} />

      {/* DELIVERECT — delivery + catering orders, customer-granted creds */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-sky-50 flex items-center justify-center shrink-0"><Bike className="w-5 h-5 text-sky-600" /></div>
          <div className="flex-1 min-w-0"><DeliverectCardBody say={say} /></div>
        </div>
      </div>

      {/* RAMP — card spend + bills; paste your own Ramp app's credentials, or OAuth when platform keys exist */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-white" /></div>
          <div className="flex-1 min-w-0"><RampCardBody say={say} /></div>
        </div>
      </div>

      {/* CLOVER — POS orders, OAuth; read-only sales + item mix */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Store className="w-5 h-5 text-emerald-600" /></div>
          <div className="flex-1 min-w-0"><CloverCardBody say={say} /></div>
        </div>
      </div>

      {/* LIGHTSPEED — POS account + sales, OAuth; read-only */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0"><Zap className="w-5 h-5 text-red-600" /></div>
          <div className="flex-1 min-w-0"><LightspeedCardBody say={say} /></div>
        </div>
      </div>

      {/* GOOGLE ANALYTICS — site + online-ordering traffic next to sales */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><BookOpenCheck className="w-5 h-5 text-amber-600" /></div>
          <div className="flex-1 min-w-0">
            <GoogleAnalyticsCardBody say={say} />
          </div>
        </div>
      </div>

      {/* INSTAGRAM */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-fuchsia-50 flex items-center justify-center shrink-0"><InstagramIcon className="w-5 h-5 text-fuchsia-600" /></div>
          <div>
            <p className="font-semibold text-slate-900">Instagram DMs</p>
            <p className="text-xs text-slate-500">DMs to your business account become to-dos ("reply to @shop — asked about 3 cases"). {instagram?.configured ? 'Connect coming right after Meta finishes reviewing our app.' : 'Built and waiting on Meta app review — connect appears here the moment it clears.'}</p>
          </div>
        </div>
      </div>

      {/* ON THE WAY — honest about what isn't live yet */}
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><CalendarClock className="w-5 h-5 text-slate-500" /></div>
          <div>
            <p className="font-semibold text-slate-900">On the way</p>
            <div className="text-xs text-slate-500 space-y-1 mt-1">
              <p><b>Grubhub</b> — no self-serve public API exists; Grubhub orders arrive through <b>Deliverect</b> (card above). Route Grubhub into Deliverect once and it flows here automatically — same for Uber Eats and DoorDash.</p>
              <p><b>Resy / OpenTable</b> — reservations &amp; guest data. API access requested; not live yet.</p>
              <p><b>Restaurant365</b> — purchasing, inventory &amp; recipe-cost sync. Partner API access requested (October 2026); not live yet. Until then, R365 exports and invoice PDFs import directly.</p>
              <p><b>Toast</b> — live for design partners via customer-granted API credentials (sales history, menus, labor). Self-serve connect UI on the way.</p>
              <p>Selling somewhere Mise doesn't connect to yet? Say so — integrations get built in the order real customers need them.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// One card per distributor portal: link the account now, finish the feed
// together in a setup session — invoices then land in Receiving untouched.
function SupplierPortalCard({ slug, name, say }: { slug: string; name: string; say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['supplier-portal', slug],
    queryFn: async () => (await api.get(`/v1/integrations/supplier/${slug}/status`)).data?.data || { connected: false },
  });
  const [user, setUser] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post(`/v1/integrations/supplier/${slug}/connect`, { username: user.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['supplier-portal', slug] }); setUser(''); say('success', `${name} linked — we finish the connection with you, then invoices flow in automatically.`); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || `Couldn't link ${name}.`),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete(`/v1/integrations/supplier/${slug}/connect`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['supplier-portal', slug] }); say('success', `${name} unlinked.`); },
  });
  return (
    <div className="card p-5 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-orange-50 flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-orange-600" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900">{name}</p>
            {st?.connected && <span className="badge badge-warning">Setup in progress</span>}
          </div>
          <p className="text-xs text-slate-500">
            {st?.connected
              ? `Linked as ${st.username}. We finish the connection with your team in one setup session — then invoices land in Receiving automatically and prices book themselves.`
              : 'Invoices flow straight from your portal account into Receiving — no re-keying. Enter your portal username to start; credentials stay with you.'}
          </p>
          {!st?.connected && (
            <div className="flex gap-2 mt-2">
              <input value={user} onChange={(e) => setUser(e.target.value)} placeholder={`${name} portal username`} className="input input-sm flex-1 max-w-xs" />
              <button onClick={() => connect.mutate()} disabled={!user.trim() || connect.isPending} className="btn btn-sm btn-primary">
                {connect.isPending ? 'Linking…' : 'Link account'}
              </button>
            </div>
          )}
          {st?.connected && <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost mt-2">Unlink</button>}
        </div>
      </div>
    </div>
  );
}


// GA4: capture the property now, the OAuth grant happens in a setup session
// (reuses the same honest setup-in-progress pattern as the supplier portals).
function GoogleAnalyticsCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const slug = 'googleanalytics';
  const { data: st } = useQuery({
    queryKey: ['supplier-portal', slug],
    queryFn: async () => (await api.get(`/v1/integrations/supplier/${slug}/status`)).data?.data || { connected: false },
  });
  const [prop, setProp] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post(`/v1/integrations/supplier/${slug}/connect`, { username: prop.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['supplier-portal', slug] }); setProp(''); say('success', 'Google Analytics linked — one setup session grants read access and traffic lands next to sales.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't link Google Analytics."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete(`/v1/integrations/supplier/${slug}/connect`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['supplier-portal', slug] }); say('success', 'Google Analytics unlinked.'); },
  });
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Google Analytics</p>
        {st?.connected && <span className="badge badge-warning">Setup in progress</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? `Linked to property ${st.username}. One setup session grants read-only access — then site and online-ordering traffic appear next to sales and in the weekly report.`
          : 'See your website and online-ordering traffic next to sales. Enter your GA4 property ID (Admin → Property Settings) to start.'}
      </p>
      {!st?.connected ? (
        <div className="flex gap-2 mt-2">
          <input value={prop} onChange={(e) => setProp(e.target.value)} placeholder="GA4 property ID, e.g. 421337965" className="input input-sm flex-1 max-w-xs" />
          <button onClick={() => connect.mutate()} disabled={!prop.trim() || connect.isPending} className="btn btn-sm btn-primary">{connect.isPending ? 'Linking…' : 'Link property'}</button>
        </div>
      ) : (
        <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost mt-2">Unlink</button>
      )}
    </>
  );
}


// Ramp connects two ways: paste your own Ramp app's client ID + secret (the
// self-serve default — validated against Ramp's token endpoint plus one read
// before saving), or the platform OAuth button when Mise's Ramp app keys exist.
function RampCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['ramp-status'],
    queryFn: async () => (await api.get('/v1/integrations/ramp/status')).data?.data || { connected: false },
  });
  const [rampId, setRampId] = useState('');
  const [rampSecret, setRampSecret] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/ramp/connect', { clientId: rampId.trim(), clientSecret: rampSecret.trim() }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['ramp-status'] });
      setRampId(''); setRampSecret('');
      say('success', `Ramp connected${res.data?.data?.businessName ? ` (${res.data.data.businessName})` : ''} — the credentials work. Hit Sync now to pull your spend and bills.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Ramp didn't accept those credentials."),
  });
  const start = useMutation({
    mutationFn: async () => (await api.get('/v1/integrations/ramp/oauth/start')).data,
    onSuccess: (res: any) => { const url = res?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Ramp connection."); },
    onError: () => say('error', 'Ramp OAuth is not configured yet.'),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/ramp/sync', {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ramp-status'] }); say('success', 'Pulled your latest Ramp spend and bills.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't read from Ramp."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/ramp/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ramp-status'] }); say('success', 'Ramp disconnected.'); },
  });
  const s2 = st?.summary;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Ramp</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> {st.businessName || 'Connected'}</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? `Your card spend and bills flow into Mise — read-only — for cost and price analysis. No accounting system in the middle.`
          : 'Pull your Ramp card spend and bills straight into Mise — read-only. Feeds the margin and price-across-stores analysis without routing through your accounting system.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {s2 && (
            <p className="text-xs text-slate-500">
              {s2.transactionCount} recent transactions{s2.topMerchants?.[0] ? ` · top: ${s2.topMerchants[0].name}` : ''}{s2.openBills ? ` · ${s2.openBills} open bills` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 mt-1">
          {st?.oauthAvailable && (
            <div className="flex items-center gap-2">
              <button onClick={() => start.mutate()} disabled={start.isPending} className="btn btn-sm btn-primary">
                {start.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Ramp'}
              </button>
              <span className="text-xs text-slate-400">You'll approve read-only access in your Ramp admin.</span>
            </div>
          )}
          <p className="text-xs text-slate-500">{st?.oauthAvailable ? 'Or create' : 'Create'} an app in Ramp → <b>Settings → Developer API</b> with the scopes listed, paste its client ID and secret — read-only. Mise checks them against Ramp before saving.</p>
          <code className="block text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-slate-600 break-all select-all">{st?.requiredScopes || 'transactions:read bills:read cards:read business:read merchants:read'}</code>
          <div className="flex gap-2 flex-wrap">
            <input value={rampId} onChange={(e) => setRampId(e.target.value)} placeholder="Client ID" className="input input-sm w-44" />
            <input value={rampSecret} onChange={(e) => setRampSecret(e.target.value)} placeholder="Client secret" type="password" className="input input-sm w-52" />
            <button onClick={() => connect.mutate()} disabled={!rampId.trim() || !rampSecret.trim() || connect.isPending} className="btn btn-sm btn-primary">
              {connect.isPending ? 'Checking…' : 'Connect'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function CloverCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['clover-status'],
    queryFn: async () => (await api.get('/v1/integrations/clover/status')).data?.data || { connected: false },
  });
  const start = useMutation({
    mutationFn: async () => (await api.get('/v1/integrations/clover/oauth/start')).data,
    onSuccess: (res: any) => { const url = res?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Clover connection."); },
    onError: () => say('error', 'Clover OAuth is not configured yet.'),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/clover/sync', {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clover-status'] }); say('success', 'Pulled your latest Clover orders.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't read from Clover."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/clover/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clover-status'] }); say('success', 'Clover disconnected.'); },
  });
  const s2 = st?.summary;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Clover</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? `Your Clover orders flow into Mise — read-only — for sales and item-mix analysis.`
          : 'Pull your Clover POS orders straight into Mise — read-only. Sales and item mix land next to your costs without any exports.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {s2 && (
            <p className="text-xs text-slate-500">
              {s2.merchantName ? `${s2.merchantName} · ` : ''}{s2.orderCount} recent orders{s2.topItems?.[0] ? ` · top: ${s2.topItems[0].name}` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : st?.oauthAvailable ? (
        <div className="flex items-center gap-2 mt-1">
          <button onClick={() => start.mutate()} disabled={start.isPending} className="btn btn-sm btn-primary">
            {start.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Clover'}
          </button>
          <span className="text-xs text-slate-400">You'll approve read-only access in your Clover account.</span>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Clover OAuth isn't configured on the platform yet — add the Clover app keys.</p>
      )}
    </>
  );
}

function LightspeedCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['lightspeed-status'],
    queryFn: async () => (await api.get('/v1/integrations/lightspeed/status')).data?.data || { connected: false },
  });
  const start = useMutation({
    mutationFn: async () => (await api.get('/v1/integrations/lightspeed/oauth/start')).data,
    onSuccess: (res: any) => { const url = res?.data?.url; if (url) window.location.href = url; else say('error', "Couldn't start the Lightspeed connection."); },
    onError: () => say('error', 'Lightspeed OAuth is not configured yet.'),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/lightspeed/sync', {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lightspeed-status'] }); say('success', 'Pulled your latest Lightspeed account details.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't read from Lightspeed."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/lightspeed/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['lightspeed-status'] }); say('success', 'Lightspeed disconnected.'); },
  });
  const s2 = st?.summary;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Lightspeed</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? `Your Lightspeed POS is linked to Mise — read-only — so sales land next to your costs.`
          : 'Connect your Lightspeed POS to Mise — read-only. Works across Retail and Restaurant lines; sales land next to your costs without any exports.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {s2?.accountName && (
            <p className="text-xs text-slate-500">Account: {s2.accountName}</p>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : st?.oauthAvailable ? (
        <div className="flex items-center gap-2 mt-1">
          <button onClick={() => start.mutate()} disabled={start.isPending} className="btn btn-sm btn-primary">
            {start.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect with Lightspeed'}
          </button>
          <span className="text-xs text-slate-400">You'll approve read-only access in your Lightspeed account.</span>
        </div>
      ) : (
        <p className="text-xs text-slate-400">Lightspeed OAuth isn't configured on the platform yet — add the Lightspeed app keys.</p>
      )}
    </>
  );
}

function DeliverectCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['deliverect-status'],
    queryFn: async () => (await api.get('/v1/integrations/deliverect/status')).data?.data || { connected: false },
  });
  const [cid, setCid] = useState('');
  const [sec, setSec] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/deliverect/connect', { clientId: cid.trim(), clientSecret: sec.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['deliverect-status'] }); setCid(''); setSec(''); say('success', 'Deliverect connected — credentials verified. Order ingestion lights up from here.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect Deliverect."),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/deliverect/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['deliverect-status'] }); say('success', 'Deliverect disconnected.'); },
  });
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Deliverect</p>
        {st?.connected && <span className="badge badge-success">Connected</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? `Connected (client ${st.clientId}) — delivery and catering orders flow in next to your in-store sales.`
          : 'Native + third-party delivery and catering orders (Grubhub, Uber Eats, DoorDash included), next to in-store sales.'}
      </p>
      {!st?.connected && (
        <ol className="text-xs text-slate-500 list-decimal ml-4 space-y-0.5 mt-1">
          <li>Email your <b>Deliverect account manager</b> (or support@deliverect.com): "Please issue API client credentials — client ID and secret — for our account." Standard request, usually same-day.</li>
          <li>Paste both below. Mise verifies them against Deliverect's token endpoint the moment you click Connect.</li>
          <li>That's it — every channel you route through Deliverect flows in with no extra setup.</li>
        </ol>
      )}
      {!st?.connected ? (
        <div className="flex gap-2 mt-2 flex-wrap">
          <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="Client ID" className="input input-sm w-44" />
          <input value={sec} onChange={(e) => setSec(e.target.value)} placeholder="Client secret" type="password" className="input input-sm w-52" />
          <button onClick={() => connect.mutate()} disabled={!cid.trim() || !sec.trim() || connect.isPending} className="btn btn-sm btn-primary">
            {connect.isPending ? 'Verifying…' : 'Connect'}
          </button>
        </div>
      ) : (
        <button onClick={() => disconnect.mutate()} className="btn btn-sm btn-ghost mt-2">Disconnect</button>
      )}
    </>
  );
}


// e-conomic (Danish ledger): the customer pastes the agreement grant token
// they generate for the Mise app inside e-conomic — validated against /self
// before it saves. Amounts mirror in DKK, unconverted, and say so.
function EconomicCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['economic-status'],
    queryFn: async () => (await api.get('/v1/integrations/economic/status')).data?.data || { connected: false },
  });
  const [token, setToken] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/economic/connect', { agreementGrantToken: token.trim() }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['economic-status'] });
      setToken('');
      say('success', `e-conomic connected${res.data?.data?.companyName ? ` (${res.data.data.companyName})` : ''} — hit Sync now and your booked invoices land in Wholesale.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect e-conomic."),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/economic/sync', {}),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['economic-status'] });
      qc.invalidateQueries({ queryKey: ['wholesale-invoices'] });
      qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      say('success', `e-conomic: ${d.invoicesCreated || 0} new invoice${d.invoicesCreated === 1 ? '' : 's'} (${d.invoicesAlreadyThere || 0} already in), ${d.customersCreated || 0} new customer${d.customersCreated === 1 ? '' : 's'}. Amounts stay in DKK — unconverted.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'e-conomic sync failed.'),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/economic/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['economic-status'] }); say('success', 'e-conomic disconnected. Mirrored invoices stay; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect e-conomic."),
  });
  const r = st?.lastSyncReport;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">e-conomic</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> {st.companyName || 'Connected'}</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? 'Your booked invoices and customers mirror into Wholesale — amounts stay in DKK, marked unconverted, so nothing downstream pretends to know an exchange rate.'
          : 'For brands keeping their books in e-conomic — booked invoices and customers mirror into Wholesale. Amounts stay in DKK; Mise never invents an exchange rate.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {r && (
            <p className="text-xs text-slate-500">
              Last sync: {r.invoicesCreated || 0} new invoices, {r.customersCreated || 0} new customers{(r.skipped || []).length ? ` · ${r.skipped.length} skipped` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Last synced {st.lastSyncAt ? new Date(st.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} disabled={disconnect.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : st?.platformApp ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">In e-conomic, grant the Mise app access — it hands you an <b>agreement grant token</b>. Paste it here; Mise checks it against e-conomic before saving.</p>
          <div className="flex items-center gap-2">
            <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Agreement grant token" type="password" className="input w-full text-sm" />
            <button onClick={() => connect.mutate()} disabled={!token.trim() || connect.isPending} className="btn btn-sm btn-primary shrink-0">{connect.isPending ? 'Checking…' : 'Connect'}</button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400">e-conomic access isn't configured on the platform yet — it arrives with Mise's e-conomic app secret.</p>
      )}
    </>
  );
}

// Bill.com: the customer's own sign-in (email, password, organization ID) plus
// the platform dev key — validated with a real v3 login before saving. AR
// invoices mirror into Wholesale; AP bills summarize right on this card.
function BillCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['bill-status'],
    queryFn: async () => (await api.get('/v1/integrations/bill/status')).data?.data || { connected: false },
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [orgId, setOrgId] = useState('');
  const [devKey, setDevKey] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/bill/connect', { username: email.trim(), password, organizationId: orgId.trim(), ...(devKey.trim() ? { devKey: devKey.trim() } : {}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bill-status'] });
      setEmail(''); setPassword(''); setOrgId(''); setDevKey('');
      say('success', 'Bill.com connected — the sign-in works. Hit Sync now and your receivables land in Wholesale.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect Bill.com."),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/bill/sync', {}),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['bill-status'] });
      qc.invalidateQueries({ queryKey: ['wholesale-invoices'] });
      qc.invalidateQueries({ queryKey: ['wholesale-customers'] });
      const ap = d.apSummary || {};
      say('success', `Bill.com: ${d.invoicesCreated || 0} new invoice${d.invoicesCreated === 1 ? '' : 's'} (${d.invoicesAlreadyThere || 0} already in).${ap.openBillCount ? ` ${ap.openBillCount} open bills totaling $${((ap.openDueCents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} summarized on the card.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Bill.com sync failed.'),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/bill/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['bill-status'] }); say('success', 'Bill.com disconnected. Mirrored invoices stay; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect Bill.com."),
  });
  const ap = st?.apSummary;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Bill.com</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> {st.orgName || st.username || 'Connected'}</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? 'Your receivable invoices mirror into Wholesale; payable bills stay summarized here — Mise builds vendor bills from received line-item documents, not AP headers.'
          : 'For brands invoicing through Bill.com — receivables mirror into Wholesale with payment status, and your payable bills summarize here for the cash picture.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {ap && (
            <p className="text-xs text-slate-500">
              {ap.openBillCount || 0} open bill{ap.openBillCount === 1 ? '' : 's'} · ${((ap.openDueCents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })} due{ap.topVendors?.[0] ? ` · largest: ${ap.topVendors[0].name}` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Last synced {st.lastSyncAt ? new Date(st.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} disabled={disconnect.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Use your production Bill.com sign-in and <b>organization ID</b> (shown at the bottom of Settings → Sync &amp; Integrations → Manage Developer Keys). Mise checks the credentials before saving.</p>
          {!st?.envKey && (
            <p className="text-xs text-slate-500">It also needs a <b>production developer key</b>, generated by a Bill.com administrator under Settings → Sync &amp; Integrations → Manage Developer Keys. Sandbox keys do not work here.</p>
          )}
          <div className="flex gap-2 flex-wrap">
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="input input-sm w-48" />
            <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" className="input input-sm w-44" />
            <input value={orgId} onChange={(e) => setOrgId(e.target.value)} placeholder="Organization ID" className="input input-sm w-44" />
            <input value={devKey} onChange={(e) => setDevKey(e.target.value)} placeholder={st?.envKey ? 'Developer key (optional)' : 'Production developer key'} type="password" className="input input-sm w-44" />
            <button onClick={() => connect.mutate()} disabled={!email.trim() || !password || !orgId.trim() || (!st?.envKey && !devKey.trim()) || connect.isPending} className="btn btn-sm btn-primary">
              {connect.isPending ? 'Checking…' : 'Connect'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Logiwa WMS: the 3PL's API address + token (+ the tenant's inventory endpoint
// if it differs from the default) — validated by actually reading the stock
// list before saving. Synced quantities update inventory items by SKU.
function LogiwaCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['logiwa-status'],
    queryFn: async () => (await api.get('/v1/integrations/logiwa/status')).data?.data || { connected: false },
  });
  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/logiwa/connect', {
      username: username.trim(), password,
      ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['logiwa-status'] });
      setBaseUrl(''); setUsername(''); setPassword('');
      const n = res.data?.data?.rowsSeen;
      say('success', `Logiwa connected — read ${n ?? 0} stock row${n === 1 ? '' : 's'}. Hit Sync now to bring on-hand up to date.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect Logiwa."),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/logiwa/sync', {}),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['logiwa-status'] });
      qc.invalidateQueries({ queryKey: ['inventory-items'] });
      const um = (d.unmatchedSkus || []).length;
      say(um ? 'error' : 'success', `Logiwa: ${d.updated || 0} item${d.updated === 1 ? '' : 's'} updated, ${d.unchanged || 0} already right.${um ? ` ${um} SKU${um === 1 ? ' isn’t' : 's aren’t'} in your inventory (${(d.unmatchedSkus || []).slice(0, 4).join(', ')}${um > 4 ? '…' : ''}) — add them, then sync again.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Logiwa sync failed.'),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/logiwa/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['logiwa-status'] }); say('success', 'Logiwa disconnected. On-hand stays where it is; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect Logiwa."),
  });
  const r = st?.lastSyncReport;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Logiwa WMS</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? 'Your 3PL’s stock counts flow into Inventory by SKU — every change writes a transaction, and SKUs Mise doesn’t know get reported, never invented.'
          : 'For brands whose 3PL runs Logiwa — warehouse stock counts update your Inventory by SKU. Ask the 3PL to create an API user (username + password); Mise signs in and reads the stock list to verify.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {r && (
            <p className="text-xs text-slate-500">
              Last sync: {r.updated || 0} updated of {r.matched || 0} matched{(r.unmatchedSkus || []).length ? ` · ${r.unmatchedSkus.length} unmatched SKUs` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Last synced {st.lastSyncAt ? new Date(st.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} disabled={disconnect.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap mt-1">
          <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="API username" className="input input-sm w-44" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" className="input input-sm w-44" />
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder={`Base URL (default ${st?.defaultBaseUrl || 'https://myapi.logiwa.com'})`} className="input input-sm w-72" />
          <button onClick={() => connect.mutate()} disabled={!username.trim() || !password || connect.isPending} className="btn btn-sm btn-primary">
            {connect.isPending ? 'Checking…' : 'Connect'}
          </button>
        </div>
      )}
    </>
  );
}

// Ongoing WMS: per-warehouse API URL, Basic-auth credentials and the goods
// owner id the 3PL assigned — validated by reading the article list before
// saving. Same SKU convention as Logiwa.
function OngoingCardBody({ say }: { say: (tone: 'success' | 'error', text: string) => void }) {
  const qc = useQueryClient();
  const { data: st } = useQuery({
    queryKey: ['ongoing-status'],
    queryFn: async () => (await api.get('/v1/integrations/ongoing/status')).data?.data || { connected: false },
  });
  const [baseUrl, setBaseUrl] = useState('');
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [goodsOwnerId, setGoodsOwnerId] = useState('');
  const connect = useMutation({
    mutationFn: () => api.post('/v1/integrations/ongoing/connect', {
      baseUrl: baseUrl.trim(), username: user.trim(), password, goodsOwnerId: parseInt(goodsOwnerId, 10),
    }),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['ongoing-status'] });
      setBaseUrl(''); setUser(''); setPassword(''); setGoodsOwnerId('');
      const n = res.data?.data?.rowsSeen;
      say('success', `Ongoing WMS connected — read ${n ?? 0} article${n === 1 ? '' : 's'}. Hit Sync now to bring on-hand up to date.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect Ongoing WMS."),
  });
  const sync = useMutation({
    mutationFn: () => api.post('/v1/integrations/ongoing/sync', {}),
    onSuccess: (res: any) => {
      const d = res.data?.data || {};
      qc.invalidateQueries({ queryKey: ['ongoing-status'] });
      qc.invalidateQueries({ queryKey: ['inventory-items'] });
      const um = (d.unmatchedSkus || []).length;
      say(um ? 'error' : 'success', `Ongoing WMS: ${d.updated || 0} item${d.updated === 1 ? '' : 's'} updated, ${d.unchanged || 0} already right.${um ? ` ${um} SKU${um === 1 ? ' isn’t' : 's aren’t'} in your inventory (${(d.unmatchedSkus || []).slice(0, 4).join(', ')}${um > 4 ? '…' : ''}) — add them, then sync again.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || 'Ongoing WMS sync failed.'),
  });
  const disconnect = useMutation({
    mutationFn: () => api.delete('/v1/integrations/ongoing/connect'),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ongoing-status'] }); say('success', 'Ongoing WMS disconnected. On-hand stays where it is; nothing new syncs.'); },
    onError: () => say('error', "Couldn't disconnect Ongoing WMS."),
  });
  const r = st?.lastSyncReport;
  return (
    <>
      <div className="flex items-center gap-2">
        <p className="font-semibold text-slate-900">Ongoing WMS</p>
        {st?.connected && <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> Connected</span>}
      </div>
      <p className="text-xs text-slate-500">
        {st?.connected
          ? 'Your EU 3PL’s article stock flows into Inventory by SKU — every change writes a transaction, and unknown SKUs get reported, never invented.'
          : 'For brands warehoused with an Ongoing WMS 3PL (EU) — article stock updates your Inventory by SKU. Ask the 3PL for API credentials and your goods owner ID.'}
      </p>
      {st?.connected ? (
        <div className="space-y-2">
          {r && (
            <p className="text-xs text-slate-500">
              Last sync: {r.updated || 0} updated of {r.matched || 0} matched{(r.unmatchedSkus || []).length ? ` · ${r.unmatchedSkus.length} unmatched SKUs` : ''}
            </p>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">Last synced {st.lastSyncAt ? new Date(st.lastSyncAt).toLocaleString('en-US') : 'never'}</span>
            <button onClick={() => sync.mutate()} disabled={sync.isPending} className="btn btn-sm btn-secondary">{sync.isPending ? 'Syncing…' : 'Sync now'}</button>
            <button onClick={() => disconnect.mutate()} disabled={disconnect.isPending} className="btn btn-sm btn-ghost text-slate-400 hover:text-red-500 ml-auto">Disconnect</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap mt-1">
          <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="API URL, e.g. https://api.ongoingsystems.se/yourwarehouse/api/v1" className="input input-sm w-80" />
          <input value={user} onChange={(e) => setUser(e.target.value)} placeholder="Username" className="input input-sm w-40" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" className="input input-sm w-40" />
          <input value={goodsOwnerId} onChange={(e) => setGoodsOwnerId(e.target.value)} placeholder="Goods owner ID" type="number" min="1" className="input input-sm w-36" />
          <button onClick={() => connect.mutate()} disabled={!baseUrl.trim() || !user.trim() || !password || !(parseInt(goodsOwnerId, 10) > 0) || connect.isPending} className="btn btn-sm btn-primary">
            {connect.isPending ? 'Checking…' : 'Connect'}
          </button>
        </div>
      )}
    </>
  );
}
